import { useDialog } from '@/common/dialog/DialogContext';
import { useForm } from '@/common/form/FormContext';
import { useFormFieldDef } from '@/common/form/hooks/useFormFieldDef';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, IconButton, InputAdornment, TextField } from '@mui/material';
import { useEffect, useState } from 'react';
import { TierFormulaBuilderDialog } from './TierFormulaBuilderDialog';
import { tokenizeFormula } from './TierFormulaUtils';
import { PYMT_BASIS_VALUE_FORMAT, useTierFormulaPalette } from './useTierFormulaPalette';

// The Tier's formula as a plain read-only text field. Its end icon opens TierFormulaBuilderDialog: a magnifier for a
// formatted view when the field can't be edited, a pencil for the full builder otherwise. The Threshold formula
// (incremental Net Sales only) is edited/shown in that same dialog rather than as its own field.
export const TierFormulaField = ({ pymtBasisIdToCode, fieldDef }) => {
  const { formValues, assignValue } = useForm();
  const { value, setValue, onBlur, error, helperText, label, disabled, variant } = useFormFieldDef(fieldDef);
  const rsrc = useRsrc();
  const { openDialog } = useDialog();

  const [progId, setProgId] = useState(null);
  const [currentProdCode, setCurrentProdCode] = useState(null);

  // Tier 0 is the Program Product's baseline/access Tier - its formula is always [ACCESS] and isn't editable
  const isTierZero = formValues.tier === 0;
  useEffect(() => {
    if (isTierZero && value !== '[ACCESS]') setValue('[ACCESS]');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTierZero]);

  // The Program Product's owning Program is what scopes the Tier variable palette
  useEffect(() => {
    const { progProdId } = formValues;
    if (!progProdId) return;
    rsrc.byId('md', 'PROG_PROD', progProdId).then((progProd) => {
      setCurrentProdCode(progProd.prodCode);
      setProgId(progProd.progId);
    });
  }, [formValues.progProdId]);

  const palette = useTierFormulaPalette(progId, currentProdCode);

  // A Threshold is only meaningful when the Tier pays on incremental Net Sales - [THRESHOLD] in the main formula
  // resolves to whatever this Tier's own thresholdFormula evaluates to (ex: a prior period's Net Sales)
  const pymtBasisCode = pymtBasisIdToCode[formValues.pymtBasisId];
  const hasThreshold = pymtBasisCode === 'INCR_NET_SALES' && !isTierZero;

  // Leaving incremental Net Sales drops the Threshold: rather than silently losing it, its formula is inlined where
  // [THRESHOLD] was used (parenthesized like the backend does at resolution time, unless it's a single token)
  useEffect(() => {
    const { thresholdFormula } = formValues;
    if (hasThreshold || !thresholdFormula) return;
    const inlined = tokenizeFormula(thresholdFormula).length > 1 ? `( ${thresholdFormula} )` : thresholdFormula;
    setValue((value ?? '').replace(/\[THRESHOLD\]/g, inlined));
    assignValue('thresholdFormula', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasThreshold]);

  const readOnly = disabled || isTierZero;

  const openBuilder = () => {
    openDialog(TierFormulaBuilderDialog, {
      data: {
        formula: value ?? '',
        thresholdFormula: formValues.thresholdFormula,
        thresholdValueFormat: hasThreshold ? PYMT_BASIS_VALUE_FORMAT[pymtBasisCode] : null,
        palette,
        readOnly,
      },
    }).then((result) => {
      if (readOnly || !result) return;
      setValue(result.formula);
      assignValue('thresholdFormula', result.thresholdFormula);
      onBlur();
    });
  };

  return (
    <TextField
      fullWidth
      variant={variant}
      label={label}
      value={value ?? ''}
      error={!disabled && error}
      helperText={helperText}
      disabled={disabled}
      sx={{ gridColumn: '1 / -1' }}
      slotProps={{
        input: {
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" onClick={openBuilder}>
                <Icon>
                  <FontAwesomeIcon style={{ width: '100%' }} icon={readOnly ? fas.faMagnifyingGlass : fas.faPencil} />
                </Icon>
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );
};
